import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft, Mail, Copy, Check } from 'lucide-react';

const BRAND = {
  navy: '#001F3F',
  red: '#FF4136',
  green: '#10B981',
  purple: '#8B5CF6',
};

const OTDQuoteEmailer = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  
  const [emailData, setEmailData] = useState({
    year: '',
    make: '',
    model: '',
    trim: '',
    vin: '',
    zip: ''
  });
  const [generatedEmail, setGeneratedEmail] = useState('');

  const handleBack = () => {
    navigate('/public-tools');
  };

  const handleChange = (field) => (e) => {
    setEmailData({...emailData, [field]: e.target.value});
    setGeneratedEmail('');
  };

  const generateOTDEmail = () => {
    const email = `Subject: Written OTD (Out-the-Door) Quote - ${emailData.year} ${emailData.make} ${emailData.model}${emailData.vin ? `, VIN ${emailData.vin}` : ''}

Hello,

I'm comparing a few stores today.

Please send me the itemized OTD (Out-the-Door) price for this vehicle:

• Year/Make/Model/Trim: ${emailData.year} ${emailData.make} ${emailData.model}${emailData.trim ? ` ${emailData.trim}` : ''}
${emailData.vin ? `• VIN: ${emailData.vin}` : '• Stock Link: [please provide]'}
• My ZIP: ${emailData.zip}

Please attach or link the buyer's order PDF showing: Vehicle price, taxes, title/registration, doc fee, and any add-ons (each on its own line).

I use the OTD total to decide where to buy.

Thank you,
[Your Name]
[Phone #]`;

    setGeneratedEmail(email);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inputClassName = "w-full px-3 py-3 text-sm font-medium rounded-xl";
  const inputStyle = { 
    border: '1.5px solid #E5E7EB',
    backgroundColor: '#FAFBFC',
    outline: 'none',
    color: BRAND.navy,
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="bg-white border-b" style={{ borderBottomColor: '#E5E7EB' }}>
        <div className="max-w-screen-sm mx-auto px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 hover:opacity-70 transition-opacity"
              style={{ color: BRAND.navy }}
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
              <span className="font-semibold text-sm">Back</span>
            </button>
            <span className="text-sm font-semibold tracking-wide">
              <span style={{ color: BRAND.navy }}>VINdicated.</span>
              <span style={{ color: BRAND.red }}>ai</span>
            </span>
          </div>
          
          <h1 className="text-xl font-extrabold tracking-tight" style={{ color: BRAND.navy }}>
            OTD Quote Emailer
          </h1>
          <p className="text-sm mt-2 leading-relaxed" style={{ color: '#6B7280' }}>
            Generate a professional email to request written out-the-door quotes from dealers.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-sm mx-auto px-5 py-6 space-y-5">
        
        {/* Form Card */}
        <div 
          className="p-5 rounded-xl space-y-4"
          style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                Year
              </label>
              <input
                type="text"
                placeholder="2024"
                value={emailData.year}
                onChange={handleChange('year')}
                className={inputClassName}
                style={inputStyle}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                Make
              </label>
              <input
                type="text"
                placeholder="Honda"
                value={emailData.make}
                onChange={handleChange('make')}
                className={inputClassName}
                style={inputStyle}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                Model
              </label>
              <input
                type="text"
                placeholder="Accord"
                value={emailData.model}
                onChange={handleChange('model')}
                className={inputClassName}
                style={inputStyle}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
                Trim (optional)
              </label>
              <input
                type="text"
                placeholder="Sport"
                value={emailData.trim}
                onChange={handleChange('trim')}
                className={inputClassName}
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
              VIN (optional)
            </label>
            <input
              type="text"
              placeholder="1HGBH41JXMN109186"
              value={emailData.vin}
              onChange={handleChange('vin')}
              className={`${inputClassName} font-mono`}
              style={inputStyle}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#374151' }}>
              Your ZIP Code
            </label>
            <input
              type="text"
              placeholder="90210"
              value={emailData.zip}
              onChange={handleChange('zip')}
              className={inputClassName}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Generate Button */}
        <button
          onClick={generateOTDEmail}
          className="w-full py-4 px-6 text-base font-bold transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2.5 hover:opacity-90"
          style={{
            backgroundColor: BRAND.navy,
            color: '#FFFFFF',
            borderRadius: '16px',
            boxShadow: '0 4px 16px rgba(0,31,63,0.25)',
          }}
        >
          <span>Generate Email</span>
          <ArrowRight className="w-5 h-5" style={{ color: BRAND.red }} strokeWidth={2.5} />
        </button>

        {/* Generated Email with Confirmation */}
        {generatedEmail && (
          <div className="space-y-3">
            {/* Confirmation Box */}
            <div 
              className="p-4 rounded-xl"
              style={{ 
                backgroundColor: `${BRAND.green}08`,
                border: `1px solid ${BRAND.green}20`,
              }}
            >
              <p className="text-sm font-semibold mb-2" style={{ color: BRAND.green }}>
                Email ready!
              </p>
              <p className="text-xs leading-relaxed" style={{ color: '#4B5563' }}>
                Copy-paste and send to 3–5 dealers (or paste into their website contact/inquiry form for fastest replies).<br/><br/>
                Expect 1–2 useful responses out of 5 — many will push for an in-person visit. Stay firm: no full numbers = no visit.<br/><br/>
                <strong style={{ color: BRAND.navy }}>Pro move:</strong> Add urgency in your note — dealers move faster when they know you&apos;re deciding soon.
              </p>
            </div>

            {/* Email Content */}
            <div 
              className="p-4 rounded-xl"
              style={{ 
                backgroundColor: '#F9FAFB',
                border: '1px solid #E5E7EB',
              }}
            >
              <pre 
                className="text-xs whitespace-pre-wrap font-mono leading-relaxed"
                style={{ color: '#374151' }}
              >
                {generatedEmail}
              </pre>
            </div>

            {/* Copy Button */}
            <button
              onClick={() => copyToClipboard(generatedEmail)}
              className="w-full py-3 font-semibold text-sm rounded-xl transition-all flex items-center justify-center gap-2"
              style={{ 
                backgroundColor: copied ? BRAND.green : '#F3F4F6',
                color: copied ? '#FFFFFF' : BRAND.navy,
              }}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Copy to Clipboard</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Quick Tips for Best Results */}
        <div 
          className="p-4 rounded-xl"
          style={{ backgroundColor: '#F8F9FB', border: '1px solid #E8EAED' }}
        >
          <p className="text-sm font-bold mb-3" style={{ color: BRAND.navy }}>
            Quick Tips for Best Results
          </p>
          <div className="space-y-3 text-xs leading-relaxed" style={{ color: '#6B7280' }}>
            <p>
              • <strong>Cast a wide net for maximum leverage</strong> — Send this email (or paste it into their website &quot;Contact Us,&quot; &quot;Get Quote,&quot; or inventory inquiry form for fastest replies) to as many dealers as possible that have the exact car (or very close match) in stock within a reasonable driving radius (e.g., 100–200 miles). Aim for 8–15+ quotes if you can find them — the more clean OTD numbers you collect, the stronger your position. It takes some upfront time, but it&apos;s way better than driving to multiple dealerships and sitting for hours. Use sites like Cars.com or AutoTrader to quickly spot who has the vehicle.
            </p>
            <p>
              • <strong>Realistic response rate:</strong> Expect useful replies from 1–2 out of 5 dealers. Many will push for an in-person visit — don&apos;t go yet. Shop from home, compare written numbers first, then visit only the winner(s) with the best quotes.
            </p>
            <p>
              • <strong>For faster and more complete replies</strong> — paste this email into the dealer&apos;s own website &quot;Contact Us,&quot; &quot;Get Quote,&quot; or inventory inquiry form instead of sending blind. Dealers prioritize these because they know you were looking at their cars online — responses are often quicker and more detailed.
            </p>
            <p>
              • <strong>Add urgency</strong> — Include a line like: &quot;I&apos;m ready to buy in the next 1–2 days and comparing offers from several dealers. If you can send a clean OTD number (including all taxes, fees, incentives, and any other charges) today or tomorrow, I&apos;ll move quickly on the best deal.&quot;
            </p>
            <p>
              • <strong>Phone backup if no email reply in 24–48 hours</strong> — Call or text the sales desk: &quot;Hi, I sent an email [or used your website form] yesterday asking for a clean OTD quote on the [Year Make Model]. I&apos;m deciding in the next day or two — if you can send the full numbers over email or phone, great. If not, no problem, I&apos;ll move to other dealers. Thanks!&quot;
            </p>
          </div>
        </div>

        {/* Tax/Fee Footer Note */}
        <p className="text-xs leading-relaxed" style={{ color: '#9CA3AF' }}>
          OTD should include all taxes (based on your ZIP), fees, and charges. Verify nothing is hidden — ask for a full breakdown if anything looks unclear.
        </p>
      </div>
    </div>
  );
};

export default OTDQuoteEmailer;
